import React, { memo } from 'react';
import { withBus } from 'react-bus';
import { Field } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy';
import MetricListContainer from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import ProductListContainer from 'src/components/BeaconV2Components/MetricList/ProductListContainer';
import { KeywordColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import StatusPill from 'src/components/BeaconV2Components/StatusPill/StatusPill';
import { EventBus } from 'src/types/utils';
import useKeywordSearchRequestBuilder from './KeywordsSearch/useKeywordSearchRequestBuilder';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { NoDataPlaceholderWithChildrenSmall } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/NoDataPlaceholder';

const segmentFields: Field[] = [
  {
    name: 'retailSales'
  },
  {
    name: 'unitsSold'
  },
  {
    name: 'retailPrice'
  }
];

const topicFields: Field[] = [
  {
    name: 'organicClicks',
    comparisonFormatter() {
      return null;
    }
  },
  {
    name: 'adClicks',
    comparisonFormatter() {
      return null;
    }
  },
  {
    name: 'totalClicks',
    comparisonFormatter() {
      return null;
    }
  },
  {
    name: 'adSpend',
    comparisonFormatter() {
      return null;
    }
  }
];

export const QUICK_EXCLUDE_EVENT = 'builderQuickExcludeEvent';

export default withBus('eventBus')(
  memo(function SegmentBuilder({ eventBus, topics }: { eventBus: EventBus; topics: boolean }) {
    const indexName = topics ? 'traffic' : 'sales';
    const fields = topics ? topicFields : segmentFields;
    const sortFieldName = topics ? 'totalClicks' : 'retailSales';

    const keywordSearchRequestBuilder = useKeywordSearchRequestBuilder(indexName);

    // Handles quick exclude button click
    const handleExcludeButtonClick = (term: string) => {
      eventBus.emit(QUICK_EXCLUDE_EVENT, term);
    };

    /**
     * Adds quick exclude column to the keywords/topics table
     */
    const additionalColumnDefs = [
      {
        headerName: 'Exclude',
        valueFormatter: (_, row) => {
          const { searchTerm } = row;
          return (
            <button
              style={{ border: 'none', background: 'none', padding: 0 }}
              onClick={() => handleExcludeButtonClick(searchTerm)}
            >
              <StatusPill color="secondary" backgroundColor="secondaryPorcelain" opacity={1}>
                Exclude
              </StatusPill>
            </button>
          );
        }
      }
    ];

    return !topics ? (
      <BasePageContainer sx={{ marginTop: '0px' }}>
        <ProductListContainer
          productGridProps={{
            showExcludeProductButton: true,
            handleExcludeProduct: handleExcludeButtonClick,
            noDataPlaceholder: (
              <Flex justifyContent="center" sx={{ marginTop: '160px' }}>
                <NoDataPlaceholderWithChildrenSmall
                  title="No matching results"
                  subtitle="Please refine your search and try again."
                />
              </Flex>
            )
          }}
          fields={fields}
          showHeader={false}
          showGrid={!topics}
          indexName={indexName}
          sortFieldName={sortFieldName}
          useTableDataProps={{
            buildRequest: keywordSearchRequestBuilder()
          }}
        />
      </BasePageContainer>
    ) : (
      <BasePageContainer sx={{ marginTop: '20px', marginBottom: `${BEACON_CHART_SPACING}px` }}>
        <MetricListContainer
          additionalColumnDefs={additionalColumnDefs}
          loading={false}
          headColumnDefinition={KeywordColumnDefinition}
          fields={fields}
          showHeader={false}
          showGrid={!topics}
          indexName={indexName}
          sortFieldName={sortFieldName}
          groupBy="searchTerm"
          skeletonRows={20}
          useTableDataProps={{
            buildRequest: keywordSearchRequestBuilder()
          }}
        />
      </BasePageContainer>
    );
  })
);
